import { useApp } from '@hb/react'
import {
  getFallbackLaborEntryData,
  LABOR_AUTOSAVE_INTERVAL_MS,
  laborEntryFields,
  LaborEntryType,
} from '@hb/shared'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-final-form'
import { saveLaborEntryFunction } from '../../../../backend/functions/assessments'
import { useProfile } from '../../../../contexts'
import { ScrollableBodyContainer } from '../../../../components/Layout/ScrollableBodyContainer'
import { InnerForm } from '../../../../components/forms/FinalForm/InnerForm'
import { OnFormSubmit } from '../../../../components/forms/FinalForm/types'
import { useLaborCharting } from '../context'
import { LaborEntryComponents } from './Components'
import { LaborEntryFooter } from './Footer'
import { LaborEntryHeader } from './Header'
import { LaborAmendmentFooter } from './LaborAmendmentFooter'
import { LaborEntryComponent, SelectedEntry } from './types'

// §4.1: Auto-save every 30 seconds — non-negotiable patient safety requirement.
// Uses a separate constant from antepartum AUTOSAVE_INTERVAL_MS (60s).
// isAutoSaveRef is set true before form.submit() fires so onSubmit knows to skip
// the amendment reason requirement.
const LaborEntryAutoSave = ({ isAutoSaveRef }: { isAutoSaveRef: React.RefObject<boolean> }) => {
  const form = useForm()
  const savingRef = useRef(false)

  useEffect(() => {
    const id = setInterval(async () => {
      if (savingRef.current || !form.getState().dirty) return
      try {
        savingRef.current = true
        isAutoSaveRef.current = true
        await form.submit()
      } finally {
        savingRef.current = false
        isAutoSaveRef.current = false
      }
    }, LABOR_AUTOSAVE_INTERVAL_MS)
    return () => clearInterval(id)
  }, [form, isAutoSaveRef])

  return null
}

const shouldUseMostRecentEntry = (entryType: LaborEntryType) => {
  switch (entryType) {
    case LaborEntryType.CERVICAL_EXAM:
    case LaborEntryType.FHR:
    case LaborEntryType.CONTRACTION:
    case LaborEntryType.VITALS:
      return true
    default:
      return false
  }
}

export const LaborEntryView = ({ selectedEntry }: { selectedEntry: SelectedEntry }) => {
  const { deselectEntry, laborEntries } = useLaborCharting()
  const { assessmentId } = useProfile()
  const { appName } = useApp()
  const EntryComponent = LaborEntryComponents[
    selectedEntry.type
  ] as LaborEntryComponent<LaborEntryType>
  const field = laborEntryFields[selectedEntry.type]

  // §4.1 dual timestamp + amendment trail:
  // amendmentReasonRef is set by LaborAmendmentFooter before manual form.submit().
  // isAutoSaveRef is set by LaborEntryAutoSave before auto-triggered form.submit().
  // Both are read in onSubmit to decide whether to require a reason.
  const amendmentReasonRef = useRef<string | undefined>(undefined)
  const isAutoSaveRef = useRef(false)

  const data = useMemo(() => {
    if (selectedEntry.existing) return selectedEntry.existing.entry.data

    if (shouldUseMostRecentEntry(selectedEntry.type)) {
      const mostRecent = laborEntries.findLast(e => e.type === selectedEntry.type) ?? null
      if (mostRecent) return mostRecent.data
    }
    return getFallbackLaborEntryData[selectedEntry.type]()
  }, [selectedEntry, laborEntries])

  const onSubmit = useCallback<OnFormSubmit>(
    async (data, _onUploadProgress, form) => {
      if (!assessmentId) return 'No assessment selected'

      const isEdit = !!selectedEntry.existing
      const isAutoSave = isAutoSaveRef.current
      const amendmentReason = amendmentReasonRef.current

      // Amendment reason is required for manual saves of existing entries.
      // createdOn immutability: the backend enforces it — never send createdOn in the payload.
      if (isEdit && !isAutoSave && !amendmentReason) {
        return 'Enter a reason for this amendment before saving.'
      }

      try {
        await saveLaborEntryFunction({
          entryId: selectedEntry.existing?.id ?? null,
          assessmentId,
          type: selectedEntry.type,
          startedAt: selectedEntry.existing
            ? selectedEntry.existing.entry.startedAt
            : selectedEntry.startedAt,
          data,
          appName,
          amendmentReason,
        })
        // Clear reason after a successful manual save so it doesn't carry over.
        amendmentReasonRef.current = undefined
        form?.reset(data)
      } catch (e: any) {
        return e?.message ?? 'Failed to save labor entry'
      }
      return undefined
    },
    [assessmentId, selectedEntry, appName],
  )

  const isExisting = !!selectedEntry.existing

  return (
    <InnerForm
      field={field}
      onSubmit={onSubmit}
      data={data}
      setUploads={() => {}}
      onClose={deselectEntry}
      closeOnSuccess
      warnNavigationIfDirty={{ formName: 'vitals' }}>
      {() => (
        <>
          {/* §4.1: 30-second auto-save — patient safety requirement */}
          <LaborEntryAutoSave isAutoSaveRef={isAutoSaveRef} />
          <ScrollableBodyContainer
            flexProps={{ bg: 'gray.50', _dark: { bg: 'gray.900' } }}
            body={<EntryComponent existingEntry={selectedEntry.existing ?? null} />}
            header={
              <LaborEntryHeader
                type={selectedEntry.type}
                existingEntry={selectedEntry.existing ?? null}
              />
            }
            footer={
              isExisting ? (
                // Existing entry: amendment reason required before manual save.
                <LaborAmendmentFooter
                  onCancel={deselectEntry}
                  amendmentReasonRef={amendmentReasonRef}
                />
              ) : (
                // New entry: no amendment reason needed.
                <LaborEntryFooter />
              )
            }
          />
        </>
      )}
    </InnerForm>
  )
}
